import { useState, useCallback } from 'react';
import type { S3File, S3Error, DownloadLinks, OsxTag, UseS3Options, UseS3Return } from '../types/assetTypes';
import { S3Service } from '../utils/s3AssetService';

/**
 * React hook for S3 operations through proxy server
 */
export const useS3 = ({ prefix }: UseS3Options = {}): UseS3Return => {
  const s3AssetService = S3Service.getInstance();
  const [s3State, setS3State] = useState({
    files: [] as S3File[],
    versions: [] as string[],
    platformVersions: { macos: [], windows: [], linux: [], all: [] } as {
      macos: string[];
      windows: string[];
      linux: string[];
      all: string[];
    },
    versionAssets: null as null | {
      version: string;
      assets: {
        darwin: S3File[];
        win32: S3File[];
        linux: S3File[];
      };
    },
    releaseTableData: [] as Array<{
      version: string;
      assetCount: number;
      published: Date;
      tags: OsxTag[] | [];
      downloadLinks: {
        linux?: string;
        mac?: string;
        win32?: string;
      };
    }>,
  });
  const [loading, setLoading] = useState(false);
  // const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<S3Error | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await s3AssetService.listFiles({ prefix });
      setS3State((prev) => ({ ...prev, files: res.files }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, [prefix]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await s3AssetService.getAllVersionsFromWeaveUpdates();
      setS3State((prev) => ({ ...prev, versions: res.versions }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVersionsByPlatform = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await s3AssetService.getVersionsByPlatform();
      setS3State((prev) => ({ ...prev, platformVersions: res.platforms }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVersionAssets = useCallback(async (version: string) => {
    setLoading(true);
    setError(null);

    try {
      const assets = await s3AssetService.getVersionAssets(version);
      setS3State((prev) => ({ ...prev, versionAssets: assets }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReleaseTableData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await s3AssetService.getReleaseTableData();
      setS3State((prev) => ({ ...prev, releaseTableData: res.releases }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, []);

  /* Batching multiple initial data loads */
  const getInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [res1, res2, res3] = await Promise.all([
        s3AssetService.getAllVersionsFromWeaveUpdates(),
        s3AssetService.getVersionsByPlatform(),
        s3AssetService.getReleaseTableData(),
      ]);
      setS3State((prev) => ({
        ...prev,
        versions: res1.versions,
        platformVersions: res2.platforms,
        releaseTableData: res3.releases,
      }));
    } catch (err) {
      setError(err as S3Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownload = useCallback(
    async (platform: 'mac' | 'win32' | 'linux', downloadLinks?: DownloadLinks | string) => {
      setLoading(true);
      setError(null);

      try {
        const filePath = typeof downloadLinks === 'string' ? downloadLinks : downloadLinks?.[platform];
        if (!filePath) return;

        // Call the getDownloadUrl API to get the signed URL
        const res = await s3AssetService.getDownloadUrl({ key: filePath });
        if (!res.success) {
          setError(new Error('Failed to get download URL'));
          setLoading(false);
        }
        if (res.downloadUrl) {
          // Create a temporary anchor element to trigger download without navigation
          const link = document.createElement('a');
          link.href = res.downloadUrl;
          link.download = filePath.split('/').pop() || 'download'; // Use filename from path
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setError(new Error('Failed to get download URL'));
          setLoading(false);
        }
      } catch (err) {
        setError(err as S3Error);
        alert('Failed to generate download link. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // TODO: *** setup uploadFile when needed ***
  //   const uploadFile = useCallback(
  //     async (file: File, _customKey?: string) => {
  //       setUploading(true);
  //       setError(null);
  //
  //       try {
  //         await s3AssetService.uploadFile({ file, prefix });
  //         // Reload files after successful upload
  //         await loadFiles();
  //       } catch (err) {
  //         setError(err as S3Error);
  //       } finally {
  //         setUploading(false);
  //       }
  //     },
  //     [prefix, loadFiles]
  //   );

  const deleteFile = useCallback(
    async (key: string, currentVersion: string) => {
      setError(null);
      setLoading(true);

      try {
        await s3AssetService.deleteFile({ key });
        // Reload files after successful deletion
        await loadFiles();
        await loadVersionAssets(currentVersion);
        setLoading(false);
      } catch (err) {
        setError(err as S3Error);
      }
    },
    [loadFiles, loadVersionAssets]
  );

  return {
    s3State,
    loading,
    error,
    loadFiles,
    loadVersions,
    loadVersionsByPlatform,
    loadVersionAssets,
    loadReleaseTableData,
    deleteFile,
    handleDownload,
    clearError,
    getInitialData,
  };
};
